import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { ReactComponent as SpeakerIcon } from '../../layout/icons/Speaker.svg';
import { accountSubscriptionManagement } from '../../layout/routes';
import { useQuery, resolvePath, useNavigate } from '../../routes';

const useStyles = makeStyles(theme => ({
  dialog: {
    position: 'absolute',
    borderRadius: theme.spacing(0.75),
    width: theme.spacing(75),
    height: theme.spacing(45),
  },
  subtext: {
    width: '100%',
    fontSize: theme.spacing(1.75),
    fontWeight: 500,
  },
  icon: {
    width: theme.spacing(15),
    height: theme.spacing(19),
    textAlign: 'center',
  },
  titleLabel: {
    flexGrow: 0,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.spacing(3),
  },
  getMoreBtn: {
    color: '#ffffff',
    background: '#1847ca',
    height: theme.spacing(4),
    alignItems: 'center',
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.spacing(1.75),
    textAlign: 'center',
    '&:hover': {
      backgroundColor: '#2A3DC7',
    },
  },
  skipBtn: {
    color: '#1847ca',
    height: theme.spacing(4),
    fontWeight: theme.typography.fontWeightMedium,
    alignItems: 'center',
    fontSize: theme.spacing(1.75),
    textAlign: 'center',
    lineHeight: 1.5,
    '&:hover': {
      color: '#2A3DC7',
    },
  },
  dlgContent: {
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
  },
  dlgTitle: {
    width: '100%',
    fontSize: theme.spacing(3),
    fontWeight: 500,
    color: '#233145',
    textAlign: 'center',
  },
  dlgActions: {
    width: '100%',
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(3),
  },
  checkboxDontShow: {
    width: theme.spacing(43.75),
    color: '#233145',
  },
  dontShowText: {
    fontSize: theme.spacing(2),
  },
}));

interface RunningOutLicenseQuotaDialogProps {
  remainUsage: number,
  lastLogin?: number,
}
const RunningOutLicenseQuotaDialog = (props: RunningOutLicenseQuotaDialogProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const { remainUsage, lastLogin } = props;
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { navigate, replaceQuery } = useNavigate();
  const orgId = Number(useQuery().get('orgId'));
  const { queryDictionary } = useQuery();
  const accountId = Number(useQuery().get('accountId'));

  useEffect(() => {
    const isSKipHasBeenClickedBefore = localStorage.getItem(`skip-reach-quota-accId-${accountId}`);
    setOpen(!JSON.parse(isSKipHasBeenClickedBefore!!));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeRunningOutLicenseQuotaDialog = (_event: any, reason: string) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const handleRedirectToSupportManagementPage = () => {
    setTimeout(() => {
      const subscriptionManagementUrl = resolvePath(
        accountSubscriptionManagement,
        undefined,
        queryDictionary(),
      );
      window.history.replaceState(null, '', subscriptionManagementUrl);
      navigate(accountSubscriptionManagement.path, replaceQuery({ orgId, accountId }));
    }, 1000);
  };

  const handleSkip = () => {
    localStorage.setItem(`skip-reach-quota-accId-${accountId}`, `${lastLogin}`);
    setOpen(false);
  };

  const handleDontShowMe = () => {
    localStorage.setItem(`not-show-reach-quota-accId-${accountId}`, `${!checked}`);
    setChecked(!checked);
  };

  return (
    <>
      <Dialog
        classes={{ paper: classes.dialog }}
        open={open}
        placeholder="center"
        fullScreen={fullScreen}
        fullWidth
        onClose={closeRunningOutLicenseQuotaDialog}
      >
        <DialogTitle id="account.home.dialog.title" className={classes.dlgTitle}>
          <FormattedMessage id="account.home.popup.title.running.out.quota" />
        </DialogTitle>
        <DialogContent id="account.home.dialog.content" className={classes.dlgContent}>
          <SpeakerIcon className={classes.icon} />
          <Box className={classes.subtext}>
            <Typography>
              <FormattedMessage
                id="account.home.popup.content.running.out.quota.guide"
                values={{
                  br: <br />,
                  remainUsage: (remainUsage * 100).toFixed(0),
                }}
              />
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions id="account.home.dialog.actions" className={classes.dlgActions}>
          <FormGroup className={classes.checkboxDontShow}>
            <FormControlLabel
              control={(
                <Checkbox
                  id="account.home.popup.checkbox"
                  checked={checked}
                  onChange={handleDontShowMe}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              )}
              label={(
                <Typography className={classes.dontShowText}>
                  <FormattedMessage id="account.home.popup.running.out.quota.dont.show.me.again" />
                </Typography>
              )}
            />
          </FormGroup>
          <Button id="account.home.popup.button.running.out.quota.skip" className={classes.skipBtn} variant="text" onClick={handleSkip}>
            <FormattedMessage id="account.home.popup.running.out.quota.skip" />
          </Button>
          <Button id="account.home.popup.button.running.out.quota.get.more" className={classes.getMoreBtn} type="button" variant="contained" onClick={handleRedirectToSupportManagementPage}>
            <FormattedMessage id="account.home.popup.running.out.quota.get.more" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RunningOutLicenseQuotaDialog;
